import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchContext } from '../../context/Context';
import ManageGrid from '../../modules/search/searchGrid/components/table/ManageGrid';
import {
  useMappingStackStore,
  useCurrentMappingLevel,
  useMappingStackBreadcrumb,
} from '../../store/mappingStackStore';
import type { MappingLevel } from '../../types/mappingDrillDown';
import { MappingBreadcrumb } from './MappingBreadcrumb';
import { mapItemsToSearchItems } from '../Table/types/index';
import { ColumnVisibilityModels } from '../Table/ColumnVisibilityModels';
import {
  useMappingSidebarColumn,
  type MappingSidebarColumnState,
} from '../../context/MappingSidebarColumnContext';
import { isEvidenceOnlyMapping } from '../../config/mappingRules';
import { MappingDrillDownProvider } from '../../context/MappingDrillDownContext';

function toManageGridMappingContent(level: MappingLevel) {
  if (!level.mappingContent) return undefined;
  return {
    ...level.mappingContent,
    isActive: true,
  };
}

function getDefaultEvidenceFilterValue(mappingContent: { name: string } | undefined) {
  if (!mappingContent) return undefined;
  if (mappingContent.name === 'numPMIDsupportingAssociation') {
    return [{ name: 'reference_type', operator: 'contains', type: 'string', value: 'PMID' }];
  }
  if (mappingContent.name === 'numCTsSupportingAssociation' || mappingContent.name === 'numCTs') {
    return [{ name: 'reference_type', operator: 'contains', type: 'string', value: 'NCTID' }];
  }
  return undefined;
}

function DrillDownMappingView({
  level,
  view,
  source,
  tab,
  panelTab,
  setSearchItems,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  dispatch,
  registerSidebarColumnState,
}: {
  level: MappingLevel;
  view: string;
  source: string;
  tab: string;
  panelTab?: string;
  setSearchItems: (items: any[]) => void;
  isInfoSidebarOpen: boolean;
  setIsInfoSidebarOpen: (v: boolean) => void;
  dispatch: any;
  registerSidebarColumnState: (s: MappingSidebarColumnState | null) => void;
}) {
  const mappingContent = level.mappingContent!;
  const keyBase = `mapping-${level.id}-${mappingContent.columVisibilityModelKey}`;
  const evidenceOnly = isEvidenceOnlyMapping(mappingContent.name);
  const [activePanel, setActivePanel] = useState<'summary' | 'evidence'>(() =>
    evidenceOnly ? 'evidence' : 'summary',
  );

  const mappingItems = mappingContent.mappingItems ?? [];
  const hasFixedDisease = mappingItems.some((i: any) => i?.filterParam === 'disease');
  const hasFixedGene = mappingItems.some((i: any) => i?.filterParam === 'gene_ncbi_id');

  function applyFixedEntityDefaultColumnHiding(model: Record<string, boolean> | undefined) {
    if (!model) return model;
    const next = { ...model };

    if (hasFixedDisease) {
      Object.keys(next).forEach(key => {
        if (key.startsWith('disease')) next[key] = false;
      });
    }

    if (hasFixedGene) {
      Object.keys(next).forEach(key => {
        if (key.startsWith('gene')) next[key] = false;
      });
    }

    return next;
  }

  const defaultSummaryModel = useMemo(
    () =>
      applyFixedEntityDefaultColumnHiding(
        ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, false),
      ),
    [mappingContent.columVisibilityModelKey, hasFixedDisease, hasFixedGene],
  );
  const defaultEvidenceModel = useMemo(
    () =>
      applyFixedEntityDefaultColumnHiding(
        ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, true),
      ),
    [mappingContent.columVisibilityModelKey, hasFixedDisease, hasFixedGene],
  );

  const [summaryColumnModel, setSummaryColumnModel] = useState<Record<string, boolean> | undefined>(
    () => defaultSummaryModel ?? undefined,
  );
  const [summaryColumnOrder, setSummaryColumnOrder] = useState<string[] | undefined>(() =>
    defaultSummaryModel ? Object.keys(defaultSummaryModel).filter(k => defaultSummaryModel[k]) : undefined,
  );
  const [evidenceColumnModel, setEvidenceColumnModel] = useState<Record<string, boolean> | undefined>(
    () => defaultEvidenceModel ?? undefined,
  );
  const [evidenceColumnOrder, setEvidenceColumnOrder] = useState<string[] | undefined>(() =>
    defaultEvidenceModel ? Object.keys(defaultEvidenceModel).filter(k => defaultEvidenceModel[k]) : undefined,
  );

  useEffect(() => {
    setSummaryColumnModel(defaultSummaryModel ?? undefined);
    setSummaryColumnOrder(
      defaultSummaryModel ? Object.keys(defaultSummaryModel).filter(k => defaultSummaryModel[k]) : undefined,
    );
    setEvidenceColumnModel(defaultEvidenceModel ?? undefined);
    setEvidenceColumnOrder(
      defaultEvidenceModel
        ? Object.keys(defaultEvidenceModel).filter(k => defaultEvidenceModel[k])
        : undefined,
    );
  }, [defaultSummaryModel, defaultEvidenceModel]);

  useEffect(() => {
    if (evidenceOnly) setActivePanel('evidence');
  }, [evidenceOnly]);

  useEffect(() => {
    const model = activePanel === 'summary' ? summaryColumnModel : evidenceColumnModel;
    const order = activePanel === 'summary' ? summaryColumnOrder : evidenceColumnOrder;
    const setModel = activePanel === 'summary' ? setSummaryColumnModel : setEvidenceColumnModel;
    const setOrder = activePanel === 'summary' ? setSummaryColumnOrder : setEvidenceColumnOrder;
    if (model != null) {
      registerSidebarColumnState({
        columnVisibilityModel: model,
        setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) =>
          setModel(prev => updater(prev ?? {})),
        columnOrder: order ?? undefined,
        setColumnOrder: (orderList: string[]) => setOrder(orderList),
        columnPanelToggle: evidenceOnly ? undefined : { active: activePanel, onChange: setActivePanel },
      });
    }
    return () => registerSidebarColumnState(null);
  }, [
    activePanel,
    summaryColumnModel,
    evidenceColumnModel,
    summaryColumnOrder,
    evidenceColumnOrder,
    registerSidebarColumnState,
    evidenceOnly,
  ]);

  const contextLabel = (() => {
    const items = mappingContent.mappingItems as Array<{ name?: string }>;
    const parts = items?.map(i => i?.name).filter(Boolean) ?? [];
    return parts.length > 0 ? parts.join(' / ') : null;
  })();

  const manageGridMappingContent = toManageGridMappingContent(level);
  const searchItemsMapped = mapItemsToSearchItems(mappingContent.mappingItems);
  const defaultEvidenceFilter = getDefaultEvidenceFilterValue(mappingContent);

  return (
    <div className="drill-down-mapping-view">
      {contextLabel && (
        <div className="titEtiqueta pt-4">
          <span>{contextLabel}</span>
        </div>
      )}

      <MappingDrillDownProvider useDrillDown={true} effectiveTab={mappingContent.tabSegment?.toUpperCase()}>
        {!evidenceOnly && (
          <div className="drill-down-mapping-summary">
            <ManageGrid
              key={`${keyBase}-summary`}
              view={view}
              source={source}
              tab={tab}
              panelTab={panelTab}
              searchItems={searchItemsMapped}
              setSearchItems={setSearchItems}
              isEvidence={false}
              isMapping={true}
              isInfoSidebarOpen={isInfoSidebarOpen}
              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
              mappingContent={manageGridMappingContent}
              dispatch={dispatch}
              restrictChemical={false}
              showRightSidebar={false}
              {...(summaryColumnModel != null && {
                columnVisibilityModel: summaryColumnModel,
                columnOrder: summaryColumnOrder,
                setColumnVisibilityModel: (
                  updater: (prev: Record<string, boolean>) => Record<string, boolean>,
                ) => setSummaryColumnModel(prev => updater(prev ?? {})),
                setColumnOrder: (order: string[]) => setSummaryColumnOrder(order),
              })}
            />
          </div>
        )}
        <div className={evidenceOnly ? 'drill-down-mapping-evidence' : 'drill-down-mapping-evidence mt-6'}>
          {!evidenceOnly && (
            <div className="titEtiqueta">
              <span>Evidence</span>
            </div>
          )}
          <ManageGrid
            key={`${keyBase}-evidence`}
            view={view}
            source={source}
            tab={tab}
            panelTab={panelTab}
            searchItems={searchItemsMapped}
            setSearchItems={setSearchItems}
            isEvidence={true}
            isMapping={true}
            isInfoSidebarOpen={isInfoSidebarOpen}
            setIsInfoSidebarOpen={setIsInfoSidebarOpen}
            mappingContent={manageGridMappingContent}
            dispatch={dispatch}
            restrictChemical={false}
            showRightSidebar={false}
            defaultFilterValue={defaultEvidenceFilter}
            {...(evidenceColumnModel != null && {
              columnVisibilityModel: evidenceColumnModel,
              columnOrder: evidenceColumnOrder,
              setColumnVisibilityModel: (
                updater: (prev: Record<string, boolean>) => Record<string, boolean>,
              ) => setEvidenceColumnModel(prev => updater(prev ?? {})),
              setColumnOrder: (order: string[]) => setEvidenceColumnOrder(order),
            })}
          />
        </div>
      </MappingDrillDownProvider>
    </div>
  );
}

export interface DrillDownTableContainerProps {
  baseLevelId: string;
  baseLabel: string;
  children: React.ReactNode;
  dispatch: any;
  panelTab?: string;
}

export function DrillDownTableContainer({
  baseLevelId,
  baseLabel,
  children,
  dispatch,
  panelTab,
}: DrillDownTableContainerProps) {
  const { view, source, tab, setSearchItems, searchItems } = searchContext();
  const { stack, currentIndex } = useMappingStackBreadcrumb();
  const resetStack = useMappingStackStore(s => s.resetStack);
  const currentLevel = useCurrentMappingLevel();
  const { setState: setMappingSidebarState } = useMappingSidebarColumn();
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = React.useState(false);
  const baseLevelRef = useRef<MappingLevel | null>(null);

  useEffect(() => {
    if (panelTab !== tab) return;

    const baseLevel: MappingLevel = {
      id: baseLevelId,
      label: baseLabel,
      isBase: true,
      searchItems: searchItems ?? [],
      panelTab,
      isEvidence: false,
    };
    baseLevelRef.current = baseLevel;
    const { stack } = useMappingStackStore.getState();
    const shouldReset =
      stack.length === 0 || (stack.length === 1 && stack[0].isBase && stack[0].id !== baseLevelId);
    if (shouldReset) {
      resetStack(baseLevel);
    }
  }, [baseLevelId, baseLabel, panelTab, tab, resetStack]);

  const isBase = currentIndex === 0;
  const level = currentLevel ?? (stack[0] ? stack[0] : null);

  return (
    <div className="drill-down-table-container">
      <MappingBreadcrumb className="mb-3" />
      {isBase && <>{children}</>}
      {!isBase && level?.mappingContent && (
        <DrillDownMappingView
          level={level}
          view={view}
          source={source}
          tab={tab}
          panelTab={panelTab}
          setSearchItems={setSearchItems}
          isInfoSidebarOpen={isInfoSidebarOpen}
          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
          dispatch={dispatch}
          registerSidebarColumnState={setMappingSidebarState}
        />
      )}
    </div>
  );
}

export default DrillDownTableContainer;
